/** The submit pattern every form shares - Q-06: **the button stays enabled, and pressing it says what
 * is wrong.**
 *
 * A form that disables its submit on a client predicate tells nobody why. A grey button reads as
 * broken, and the E2E walks found it on eight forms (Profile's blank name, the Auditor invitation's
 * `Access ends`, Scope 3's empty reason, a target's empty fields, the manual figure's short basis, the
 * site correction): the person had to guess which of six boxes the button was waiting for. This is
 * the one home for the better pattern:
 *
 * 1. The button is **not** disabled for a missing or invalid field. (It is still disabled while a
 *    request is in flight, and for a reason the person cannot fix by typing - say so beside it.)
 * 2. Pressing it calls `run(rules)`. Each failed rule puts **its own sentence beside its own field**
 *    (`<FieldError>`), `aria-invalid` on the field, and focus goes to the **first** such field in
 *    the order the rules are listed (WCAG 3.3.1 and 3.3.3: identify the error in text, and suggest
 *    the fix). Nothing is sent.
 * 3. The sentence goes when the person edits that field (`touch`), so a corrected box does not keep
 *    accusing them.
 * 4. A **server** refusal is placed the same way: `refuse(error, { quantity: "amount" })` maps the
 *    API's field names to this form's and returns what it could not place, which the form shows as the
 *    ordinary `WriteFailed` sentence. A server 422 for "4,200" (before the client learned to take the
 *    comma off) therefore lands beside the Amount box, not as "That could not be recorded."
 *
 * **The server stays the authority.** A rule here is the same check the server makes, said before the
 * request instead of after it; it is never a substitute for the server's, and a rule that disagrees
 * with the server is fixed by deleting it.
 */

import { useCallback, useId, useState } from "react";

import { Refused } from "../api/refusal";

/** One rule: the field it is about, whether it holds, and what to say when it does not. */
export interface FieldRule {
  /** A stable name for the field, used as its `data-field`. */
  field: string;
  ok: boolean;
  /** The sentence, in the form's words: what is wrong and what to do. */
  message: string;
}

/** What a field element needs to take part: spread it on the input, select or textarea. */
export interface FieldProps {
  "data-field": string;
  "aria-invalid": true | undefined;
  "aria-describedby": string | undefined;
}

export interface FormChecks<T extends HTMLElement = HTMLFormElement> {
  /** The element the hook's second result (`attach`) was given, once it has mounted. */
  readonly element: T | null;
  readonly errors: Readonly<Record<string, string>>;
  /** Evaluate the rules in order. `true` when every rule holds. Otherwise records each failed rule's
   * sentence, moves focus to the first, and returns `false`: the caller then sends nothing. */
  run: (rules: readonly FieldRule[]) => boolean;
  /** The person edited this field: its sentence goes. */
  touch: (field: string) => void;
  clear: () => void;
  /** Place a server refusal beside its fields. `names` maps the API's field name to this form's.
   * Returns `true` when at least one sentence was placed (and focus moved), so the caller can leave
   * the generic sentence out. */
  refuse: (error: unknown, names?: Readonly<Record<string, string>>) => boolean;
  field: (name: string) => FieldProps;
  /** The id of the sentence for `name`. */
  errorId: (name: string) => string;
  /** What `<FieldError>` needs for `name`, to be spread: `<FieldError {...checks.say("amount")} />`. */
  say: (name: string) => { field: string; message: string | undefined; id: string };
}

function focusField(root: HTMLElement | null, field: string): void {
  const target = root?.querySelector<HTMLElement>(`[data-field="${field}"]`);
  target?.focus();
}

/** `const [checks, attach] = useFormChecks();` then `<form ref={attach}>` around the fields.
 *
 * `attach` is returned beside the checks and not inside them, on purpose: it is passed to a `ref`
 * prop, and React's lint refuses to let anything derived from the same object be read during render.
 * It is a callback that stores the element in state, so `checks.element` is an ordinary value. */
export function useFormChecks<T extends HTMLElement = HTMLFormElement>(): readonly [
  FormChecks<T>,
  (element: T | null) => void,
] {
  const [element, attach] = useState<T | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const prefix = useId();

  const errorId = useCallback((name: string) => `${prefix}-${name}-error`, [prefix]);

  const run = useCallback(
    (rules: readonly FieldRule[]): boolean => {
      const failed = rules.filter((rule) => !rule.ok);
      // First sentence per field wins, in the order listed: two rules on one box say the first.
      const next: Record<string, string> = {};
      for (const rule of failed) if (!(rule.field in next)) next[rule.field] = rule.message;
      setErrors(next);
      const first = failed[0];
      if (!first) return true;
      focusField(element, first.field);
      return false;
    },
    [element],
  );

  const touch = useCallback((field: string) => {
    setErrors((current) => {
      if (!(field in current)) return current;
      const { [field]: _gone, ...rest } = current;
      return rest;
    });
  }, []);

  const clear = useCallback(() => setErrors({}), []);

  const refuse = useCallback(
    (error: unknown, names: Readonly<Record<string, string>> = {}): boolean => {
      if (!(error instanceof Refused) || error.fieldSentences.length === 0) return false;
      const placed: Record<string, string> = {};
      let first = "";
      for (const { field, sentence } of error.fieldSentences) {
        const here = names[field] ?? field;
        // Only a field this form knows: a sentence for a box that is not on the screen belongs in
        // the form's general failure, so it is not placed.
        if (!element?.querySelector(`[data-field="${here}"]`)) continue;
        if (!(here in placed)) placed[here] = sentence;
        if (first === "") first = here;
      }
      if (first === "") return false;
      setErrors(placed);
      focusField(element, first);
      return true;
    },
    [element],
  );

  const field = useCallback(
    (name: string): FieldProps => ({
      "data-field": name,
      "aria-invalid": name in errors ? true : undefined,
      "aria-describedby": name in errors ? errorId(name) : undefined,
    }),
    [errors, errorId],
  );

  const say = useCallback(
    (name: string) => ({ field: name, message: errors[name], id: errorId(name) }),
    [errors, errorId],
  );

  return [{ element, errors, run, touch, clear, refuse, field, errorId, say }, attach] as const;
}
